import { PrepTemplate, SummaryTemplate } from "@epicbrief/shared/types/index"

export const FIELD_MIN_LENGTH = 2
export const FIELD_MAX_LENGTH = 1000

export const SEARCH_PARAM_KEYS = {
  MEETING_ID: "meetingId",
  CODE: "code",
  TEMPLATE_FILTER: "templateFilter",
  TEMPLATE_ID: "summaryTemplateId",
}

export const IS_MOBILE_MEDIA_QUERY = {
  query: "(max-width: 786px)",
}

export const SUBSCRIPTIONS = ["pro", "free", "xl"]

export const SIGN_IN_EMAIL_KEY = "emailForSignIn"
export const SLACK_AUTH_COMPLETE_KEY = "slackAuthComplete"

export const PATHS = {
  DEAL: "/deal",
  MEETING: "/meeting",
  SUMMARY: "/summary",
  TRANSCRIPT: "/transcript",
  SELECT_TEMPLATE: "/select-template",
  COACHING: "/coaching",
  CRM: "/crm",
  ID: "/:id",
  EMAIL: "/email",
}

export const ROUTES = {
  LANDING: "/",
  SECURITY: "/security",
  LOGIN: "/login",
  SIGN_IN_EMAIL: "/sign-in-email",
  CONNECT_CALENDAR: "/connect-calendar",
  CONNECT_VIDEO: "/connect-video",
  CONNECT_CHROME: "/connect-chrome",
  CONNECT_SLACK: "/connect-slack",
  SLACK: "/slack",
  SETTINGS: {
    INDEX: "/settings",
    TEMPLATES: "/settings/templates",
    INTEGRATIONS: "/settings/integrations",
    MANAGE_USERS: "/settings/manage-users",
    CRM_FIELDS_MAPPING: "/settings/crm-fields-mapping",
  },
  MEETING: {
    MAIN: PATHS.MEETING + PATHS.ID,
    SELECT_TEMPLATE: PATHS.MEETING + PATHS.ID + PATHS.SELECT_TEMPLATE,
    SUMMARY: PATHS.MEETING + PATHS.ID + PATHS.SUMMARY,
    TRANSCRIPT: PATHS.MEETING + PATHS.ID + PATHS.TRANSCRIPT,
    COACHING: PATHS.MEETING + PATHS.ID + PATHS.COACHING,
    CRM: PATHS.MEETING + PATHS.ID + PATHS.CRM,
    EMAIL: PATHS.MEETING + PATHS.ID + PATHS.EMAIL,
  },
  DEAL: {
    MAIN: PATHS.DEAL,
    SUMMARY: PATHS.DEAL + PATHS.ID + PATHS.SUMMARY,
  },
  TEMPLATES: "/templates",
  PUBLIC_SUPPORT: "/technical-support",
  SUPPORT: "/support",
  SUBSCRIPTION: "/subscription",
  ACCOUNTS: "/accounts",
  HOME: "/home",
  MEETINGS: "/meetings",
  ADD_MEETING: "/addmeeting",
  FALL_BACK: "*",
  NOT_FOUND: "/404",
  LAB: "/lab",
  WORKFLOWS: "/workflows",
  PROJECTS: "/projects",
  PROJECT: "/projects/:projectId",
  CHATS: "/chats",
  AGENTS: "/agents",
}

export const TEST_IDS = {
  MENUS: {
    BLOCK_CONTROL: {
      OPEN: "block-control-menu-open",
      DUPLICATE: "block-control-duplicate",
      TURN_INTO: "block-control-turn-into",
      DELETE: "block-control-delete",
    },
  },
  BUTTONS: {
    NEW_MEETING: "new-meeting-button",
    ADD_MEETING: "add-meeting-button",
    SPEAKER: "speaker-button",
    SUMMARIZE_TEST: "test-summarize-button",
    SELECT_TEMPLATE: "select-template-button",
    SKIP: "skip-button",
    EDIT_SAVED_MEETING: "edit-saved-meeting-button",
    ASK_AI: {
      START: "ask-ai-start",
      SUBMIT_DUMMY: "ask-ai-dummy",
      SUBMIT_PROD: "ask-ai-prod",
      BACK: "ask-ai-back",
      DONE: "ask-ai-done",
      CLOSE: "ask-ai-close",
      SUGGESTION: "ask-ai-suggestion",
    },
    CHANGE_TEMPLATE: "change-template-button",
    CHANGE_TEMPLATE_MODAL: "change-template-modal",
    SELECT_MODAL_TEMPLATE: "select-modal-template",
    SAVE_TEMPLATE: "save-template-button",
    ALERT_CONFIRM: "alert-confirm-button",
    DATE_FILTER: {
      ALL: "date-filter-all-button",
      TODAY: "date-filter-today-button",
    },
    CHROME_PAGE: "connect-chrome-page",
    SHARE_SUMMARY_SLACK: "share-summary-slack-button",
    SHARE_SUMMARY_EMAIL: "share-summary-email-button",
    CLOSE_MODAL: "close-modal-button",
    SHARE_EMAIL: "share-email-button",
    MEETING_MENU: "meeting-menu-button",
    DELETE_MEETING: "delete-meeting-button",
  },
  FIELDS: {
    PASTE_TRANSCRIPT: "paste-transcript-field",
    TEXT: "form-text-field",
    EDIT_TEXT: "editor-text-field",
    SUMMARY: "summary-text-area",
    ASK_AI_QUESTION: "ask-ai-question-field",
    SEARCH: "search-text-field",
    SHARE_BLOCK: "share-block-checkbox",
    DATE_PICKER: "date-picker-field",
  },
  CONTAINERS: {
    SUMMARY_BLOCK: "summary-block-container",
    TABLE_HEADER: "data-table-header",
  },
  CONTENTS: {
    ASK_AI_RESULT_TITLE: "ask-ai-result-title",
    ASK_AI_RESULT_ITEM: "ask-ai-result-item",
    MEETINGS_TABLE_TIME: "meetings-table-start-time",
    MEETINGS_TABLE_HEADER: "meetings-table-header",
    MEETINGS_TABLE_CHECKBOX: "meetings-table-select-checkbox",
    MEETINGS_TABLE_ACTIONS: "meetings-table-actions",
    MEETINGS_TABLE_ACTIONS_DELETE: "meetings-table-actions-delete",
    MEETINGS_TABLE_DELETE_MODAL: "meetings-table-delete-modal",
    MEETINGS_TABLE_RESIZER: "meetings-table-resizer",
    MEETINGS_TABLE_PAGINATION: "meetings-table-pagination",
    SHARE_BLOCK_HEADING: "share-block-heading",
    SHARE_BLOCK_ITEM: "share-block-item",
    FIRST_NAMES_STRING: "participants-first-name-string",
    FULL_NAMES_LIST: "participants-full-name-list",
  },
  LINKS: {
    SINGLE_MEETING: "link-to-single-meeting",
    CHROME_STORE: "link-to-chrome-store",
  },
}

export const MESSAGES = {
  MEETING_SAVED: "Meeting saved",
  ACCOUNT_SAVED: "Account saved",
  DEAL_SAVED: "Deal saved",
  TEMPLATE_SAVED: "Template saved",
  TEMPLATE_DELETED: "Template deleted",
  MESSAGE_SENT: "Message sent",
}

export const SYSTEM_PREP_TEMPLATES: PrepTemplate[] = [
  {
    name: "Meeting preparation",
    description: "A template to help you prepare for your next meeting.",
    instructions: "",
    fields: [
      {
        name: "Insights",
        question:
          "Please analyze the LinkedIn profiles of the participants involved in our upcoming sales call. The purpose of this analysis is to gather essential information that will inform and enhance our sales strategy. Focus your analysis on the following key aspects:\nCorporate Objectives Pursued: Examine any details that suggest the corporate objectives the individual is likely focused on. This could include their current role's responsibilities, their involvement in specific projects, or any goals and missions mentioned in their personal or company profile.\nBusiness Strategies Implemented: Identify indications of business strategies that the person is likely implementing in their position. Look for clues in their listed experiences, achievements, or the way they describe their role and contributions to their company.\nExternal Capabilities Sought: Determine the type of external capabilities, such as skills, technologies, services, or products, the person might be looking for to achieve their corporate objectives. This can be inferred from their past procurement history, technologies they're interested in, or problems they're actively discussing or solving.\nAwareness of Company Business Challenges: Assess what business challenges their company might be facing, which the person is likely aware of or directly addressing. Information for this can be found in their posts, comments on industry trends, or in discussions they are part of, which reflect current challenges in their industry or specific pain points their company might be experiencing.\nSynthesize this information to create a profile that helps us understand the customer's key business objectives, strategies, needs, and challenges. Based on your analysis, provide recommendations for how our products or services can be positioned as solutions to their specific requirements. Additionally, suggest potential conversation points and strategic angles we can use during the sales call to align our offering with their goals and challenges. Start each response with the name of the participant are their roles as follows: full name, role: answer.",
      },
      {
        name: "Questions to ask",
        question:
          "You are a sales trainer. Consider information available about the person you have information about, product being sold and consultative selling approach. What would be the questions I could ask this person to deeper understand how I can support their business with my services. Customize each question to the product the seller is selling (Product description) and who they are selling the product to (Customer).",
      },
      {
        name: "Pain points",
        question:
          "Considering the seller product, what are the most relevant pain points for this customer company?",
      },
      {
        name: "Objections",
        question:
          "When engaging in sales discussions for complex solutions like digital transformation projects, a key step is to anticipate potential objections from the buyer. Conduct an analysis based on available data, such as LinkedIn profiles of key decision-makers and the specifics of your product. Your objective is to formulate a list of Objection Hypotheses, each tailored to the decision-maker’s profile, their company’s current activities, and the unique aspects of your product. Ensure each objection hypothesis is insightful and specific, reflecting a deep understanding of the buyer’s background and their company’s possible concerns regarding your product.\nStructure your analysis as follows:\nList of Objection Hypotheses: Create a comprehensive list of potential objections. These should be based on factors like the decision-maker's professional interests, recent company developments, industry trends, and the specific features and implications of your product. Consider objections related to project overlap, budget constraints, strategic alignment, or doubt about the product's benefits.\nFocus your analysis on articulating these Objection Hypotheses, providing a clear and concise understanding of the potential challenges you might face in the sales conversation.",
      },
    ],
    id: "sys-prep-default",
    ownerRefs: [],
    teamRefs: [],
  },
]

export const SYSTEM_NOTE_TEMPLATES: SummaryTemplate[] = [
  {
    name: "Customer Discovery",
    description: "A summary of your customers needs and goals.",
    instructions: "This is a transcript of a business to business sales call.",
    fields: [
      {
        name: "Customer needs",
        question:
          "What are the needs of the customer? Never start a bullet point with 'the company' or anything similar, start the sentence with the verb.",
        sectionToSearch: "all",
        speakersToSearch: "customer only",
        blockType: "bullet",
      },
      {
        name: "Customer goals",
        question:
          "What are the goals and objectives of the customer? Never start a bullet point with 'the company' or anything similar, start the sentence with the verb.",
        sectionToSearch: "all",
        speakersToSearch: "customer only",
        blockType: "bullet",
      },
      {
        name: "Next steps",
        question: "What are the action points?",
        sectionToSearch: "last 1000 words",
        speakersToSearch: "all",
        blockType: "actionItems",
      },
    ],
    id: "sys-basic-sales",
    ownerRefs: [],
    teamRefs: [],
  },
  {
    name: "1:1 Meeting",
    description:
      "Summary on what is pending, action items, timeline and issues to review.",
    instructions: "This is a transcript of a business to business sales call.",
    fields: [
      {
        name: "Update on what is pending",
        question:
          "You are a secretary and executive assistant for an executive. Your task is to document the 1:1 discussion an executive had with their employee and colleague. The output will be used by a executive to track progress and keep the other person accountable and follow-up to make sure things are getting done. You are interested to document  only:\n\n- What tasks are work in progress since last time spoke? \n\nIf information is not available then answer N/A. Example output:\n\n- Pending Task #1: Create a roadmap for hiring\n- Pending Task #2: Evaluate ERP vendors\n- Pending Task #3: Create presentation sales people can use in their customer meetings",
        sectionToSearch: "all",
        speakersToSearch: "all",
        blockType: "bullet",
      },
      {
        name: "New action items",
        question:
          "You are a secretary and executive assistant for an executive. Your task is to document the 1:1 discussion an executive had with their employee and colleague. The output will be used by a executive to track progress and keep the other person accountable and follow-up to make sure things are getting done. You are interested to document  only:\n\n- Which tasks and action items are new? What needs to be done?\n\nIf information is not available then answer N/A. Example output:\n\n- New Task #1: Contact local payroll provider in Columbia\n- New Task #2: Organise a team event in December",
        sectionToSearch: "all",
        speakersToSearch: "all",
        blockType: "bullet",
      },
      {
        name: "Timeline for Delivery",
        question:
          "You are a secretary and executive assistant for an executive. Your task is to document the 1:1 discussion an executive had with their employee and colleague. The output will be used by a executive to track progress and keep the other person accountable and follow-up to make sure things are getting done. You are interested to document  only:\n\n- When will pending and new tasks be completed?\n\nIf information is not available then answer N/A. Example output:\n\n- Pending Task #1: Done\n- Pending Task #2: End of this week\n- New Task #1: End of November\n- New Task #2: Mid-December",
        sectionToSearch: "all",
        speakersToSearch: "all",
        blockType: "bullet",
      },
      {
        name: "Any issues to review",
        question:
          "You are a secretary and executive assistant for an executive. Your task is to document the 1:1 discussion an executive had with their employee and colleague. The output will be used by a executive to track progress and keep the other person accountable and follow-up to make sure things are getting done. You are interested to document  only:\n\n- What are the issues raised in the conversation?\n\nIf information is not available then answer N/A. Example output:\n\n- Brooke: Things aren't progressing fast enough\n- <employee name from transcript>: Too many things to juggle, no support from executive team",
        sectionToSearch: "all",
        speakersToSearch: "all",
        blockType: "bullet",
      },
    ],
    id: "sys-1on1",
    ownerRefs: [],
    teamRefs: [],
  },
  {
    name: "The Gist",
    description: "The gist of the meeting and next steps.",
    instructions: "This is a transcript of a business to business sales call.",
    fields: [
      {
        name: "The gist",
        question: "What are the key takeaways from the meeting?",
        sectionToSearch: "all",
        speakersToSearch: "all",
        blockType: "bullet",
      },
      {
        name: "Next steps",
        question: "What are the next steps?",
        sectionToSearch: "last 1000 words",
        speakersToSearch: "all",
        blockType: "actionItems",
      },
    ],
    id: "sys-presentation-feedback",
    ownerRefs: [],
    teamRefs: [],
  },
  {
    name: "Follow up Meeting",
    description:
      "Summary of a follow up meeting: main points, key decisions, next steps.",
    instructions: "This is a transcript of a business to business sales call.",
    fields: [
      {
        name: "Main points",
        question: "What were the main discussion points?",
        sectionToSearch: "all",
        speakersToSearch: "all",
        blockType: "bullet",
      },
      {
        name: "Key decisions",
        question: "What decisions were made?",
        sectionToSearch: "all",
        speakersToSearch: "customer only",
        blockType: "bullet",
      },
      {
        name: "Action items",
        question: "What are action points?",
        sectionToSearch: "last 1000 words",
        speakersToSearch: "all",
        blockType: "actionItems",
      },
    ],
    id: "sys-follow-up-meeting",
    ownerRefs: [],
    teamRefs: [],
  },
  {
    name: "Team Meeting",
    description: "A summary for your team, key decisions and action items.",
    instructions: "This is a transcript of a business to business sales call.",
    fields: [
      {
        name: "Main points",
        question: "What were the main discussion points?",
        sectionToSearch: "all",
        speakersToSearch: "all",
        blockType: "bullet",
      },
      {
        name: "Key decisions",
        question: "What decisions were made?",
        sectionToSearch: "all",
        speakersToSearch: "all",
        blockType: "bullet",
      },
      {
        name: "Action items",
        question: "What are action points?",
        sectionToSearch: "all",
        speakersToSearch: "all",
        blockType: "actionItems",
      },
    ],
    id: "sys-team-meeting",
    ownerRefs: [],
    teamRefs: [],
  },
  {
    name: "Standup",
    description:
      "Summary for ongoing project: main takeaways, blockers, action items.",
    instructions: "This is a transcript of a business to business sales call.",
    fields: [
      {
        name: "Main takeaways",
        question: "Summarise the main takeaways from the meeting",
        sectionToSearch: "all",
        speakersToSearch: "all",
        blockType: "bullet",
      },
      {
        name: "Standup by team member",
        question:
          "I want you to act as a team leader. For each team member summarise the progress they've made since the last Stand-up, their plan until the next Stand-up, and whether there’s anything blocking their progress.",
        sectionToSearch: "all",
        speakersToSearch: "all",
        blockType: "bullet",
      },
      {
        name: "Blockers and challenges",
        question: "Identify any blockers or challenges mentioned",
        sectionToSearch: "all",
        speakersToSearch: "all",
        blockType: "bullet",
      },
      {
        name: "Next steps",
        question:
          "Summarise what each team member will do before the next meeting",
        sectionToSearch: "last 1000 words",
        speakersToSearch: "all",
        blockType: "actionItems",
      },
    ],
    id: "sys-standup",
    ownerRefs: [],
    teamRefs: [],
  },
]

export const PROMPT_SUGGESTIONS = [
  {
    title: "Key Discussion Points",
    prompt: "What were the primary subjects addressed during the meeting?",
  },
  {
    title: "Client's Needs and Expectations",
    prompt:
      "What needs, pain points, or expectations did the client express about the product or service?",
  },
  {
    title: "Client's Feedback",
    prompt:
      "What were the client's responses, objections, or questions regarding the proposed solutions?",
  },
  {
    title: "Potential Challenges",
    prompt:
      "Were there any potential hurdles or challenges mentioned that could impact the business relationship with the client?",
  },
  {
    title: "Key Takeaways and Learnings",
    prompt:
      "What key insights or learnings can be drawn from the discussions during the meeting?",
  },
  {
    title: "Overall Impression and Relationship Status",
    prompt:
      "Based on the tone and content of the meeting, how would one describe the current relationship with the client and the overall impression of the meeting?",
  },
]
